// hooks/useECommerceStatus.tsx
import { useFetchApi } from "./useFetchApi";
import { Language } from "../utils/localization/languages/types";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { customLocationValuesFromSearch } from "src/utils/localization/geolocation/geolocationLoader";
import { match } from "ts-pattern";
import { useQuerySearch } from "src/utils/useQueryParams";
import { isPresent } from "src/utils/checks";
import { CustomPromoState, useCustomPromo } from "components/CustomPromo/hooks";

type EcommerceEnabledResponse = {
  type: "ECommerceEnabled";
  ecommerceUrl: string;
  priceConfigUrl: string;
};

type PriceConfigEnabledResponse = {
  type: "PriceConfigEnabled";
  url: string;
};

type CruiseNotEnabledResponse = {
  type: "CruiseNotEnabled";
};

type IsEnabledApiResponse =
  | Readonly<PriceConfigEnabledResponse>
  | Readonly<EcommerceEnabledResponse>
  | Readonly<CruiseNotEnabledResponse>;

const apiUrlFor = (
  cruiseCode: string,
  country: string | undefined,
  language: Language
): string => {
  const baseUrl =
    process.env.GATSBY_PRICE_CONFIGURATOR_IS_ENABLED_API_URL ||
    "http://localhost:3000/dev/isEnabled/";
  const apiUrl = new URL(cruiseCode, baseUrl);

  apiUrl.searchParams.append("locale", language.code);
  if (country) apiUrl.searchParams.append("country", country);
  return apiUrl.href;
};

type PriceConfig = Omit<PriceConfigEnabledResponse, "type"> & {
  type: "priceconfig";
};

type Ecommerce = Omit<EcommerceEnabledResponse, "type" | "url"> & {
  type: "ecommerce";
};

type Disabled = {
  type: "disabled";
};

type Loading = {
  type: "loading";
};

export type ECommerceStatus = Loading | PriceConfig | Ecommerce | Disabled;

export const useECommerceStatus = (
  cruiseCode: string | undefined,
  isCruiseWaitlist: boolean
): ECommerceStatus => {
  const language = useLanguage();
  const searchParams = useQuerySearch();
  const { promoState, promoIsValid } = useCustomPromo();

  const params = !isPresent(searchParams)
    ? {}
    : customLocationValuesFromSearch(searchParams);
  const overriddenCountryCode = params.countryCode;

  const country = overriddenCountryCode ? overriddenCountryCode : undefined;

  const { status, data, isLoading } = useFetchApi<IsEnabledApiResponse>(
    isCruiseWaitlist || !isPresent(cruiseCode)
      ? undefined
      : apiUrlFor(cruiseCode, country, language),
    ["ecommerce-is-enabled", cruiseCode]
  );

  if (isCruiseWaitlist) return { type: "disabled" as const };
  if (isLoading) return { type: "loading" };

  if (status == "success" && data) {
    return match(data)
      .with({ type: "PriceConfigEnabled" }, (params) => ({
        ...params,
        type: "priceconfig" as const
      }))
      .with({ type: "ECommerceEnabled" }, (params) => ({
        ...params,
        ecommerceUrl: addCustomPromoQueryString(
          params.ecommerceUrl,
          promoState,
          promoIsValid
        ),
        type: "ecommerce" as const
      }))
      .with({ type: "CruiseNotEnabled" }, () => ({ type: "disabled" as const }))
      .exhaustive();
  }

  return { type: "disabled" };
};

const addCustomPromoQueryString = (
  url: string,
  promoState: CustomPromoState,
  promoIsValid: boolean
) => {
  if (promoIsValid) {
    const newUrl = new URL(url);
    newUrl.searchParams.set("pc", promoState.promoCode);
    return newUrl.toString();
  }
  return url;
};
